import { useMutation } from '@tanstack/react-query';
import {
	transitionAuthScope,
	SCOPE_TRANSITION_STATUS,
} from '@/features/auth/session/transitionAuthScope';
import { normalizeScope } from '@/features/auth/domain/session/scope';
import { getAuthSession } from '@/features/auth/session';
import { sessionStore } from '@/features/auth/domain/session/sessionStore';
import { clearAllStoresAndLocalStorage } from '@/legacy/utils';

type DelegationAccessVars = { delegations: number[] };
type DelegationAccessResult = { activated: boolean };

export type DelegationAccessMutationCallbacks = {
	onSuccess?: (data: DelegationAccessResult, vars: DelegationAccessVars) => void;
	onError?: (error: unknown, vars: DelegationAccessVars) => void;
};

// Callbacks are wired into useMutation's own config (not passed to mutate()),
// so they always fire once the request settles — mutate()-level callbacks are
// dropped by TanStack Query when the calling component has since unmounted.
//
// Error toasting is the caller's job (via the onError callback), not this hook's — it used to
// toast here AND in the caller, showing the same error twice.
//
// Delegations only ever change WITHIN the current active company — the target
// scope is the active company's id plus the new delegation selection.
// transitionAuthScope owns the endpoint choice and the scoped cache commit.
export const useDelegationAccessMutation = (callbacks?: DelegationAccessMutationCallbacks) => {
	return useMutation({
		mutationFn: async ({ delegations }: DelegationAccessVars): Promise<DelegationAccessResult> => {
			const { defaultCompanyId } = getAuthSession();
			const scope = normalizeScope(defaultCompanyId, delegations);
			if (!scope) throw new Error('No active company to scope delegations to');

			const result = await transitionAuthScope(scope);
			if (result.status === SCOPE_TRANSITION_STATUS.FAILED) {
				throw result.error ?? new Error('Failed to switch delegations');
			}
			return { activated: result.status !== SCOPE_TRANSITION_STATUS.STALE };
		},
		onSuccess: (data, vars) => {
			// A STALE result means this transition was superseded by a newer one
			// before it could activate — the superseding transition already owns
			// the store reset and navigation. See useFakeAccessMutation.ts for why
			// persistToStorage() must follow clearAllStoresAndLocalStorage() here.
			if (!data.activated) return;

			clearAllStoresAndLocalStorage();
			sessionStore.getState().persistToStorage();
			callbacks?.onSuccess?.(data, vars);
		},
		onError: (error, vars) => {
			callbacks?.onError?.(error, vars);
		},
	});
};
